import { useState } from 'react'
import { Card, Row, Col, Badge, Button, Table, Tabs, Tab, ProgressBar, Modal, Alert, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX, getMonthStartDateOnlyMx } from '../../utils/apiDateFormat'
import BudgetLineForm from './BudgetLineForm'
import BudgetExecution from './BudgetExecution'
import BudgetModifications from './BudgetModifications'
import BudgetAlerts from './BudgetAlerts'
import BudgetConfig from './BudgetConfig'
import BudgetCharts from './BudgetCharts'
import { formatCurrency } from '../../utils/formatters'

const BudgetDetail = ({ budget: initialBudget, onEdit }) => {
  const queryClient = useQueryClient()
  const [showLineForm, setShowLineForm] = useState(false)
  const [selectedLine, setSelectedLine] = useState(null)
  const [activeTab, setActiveTab] = useState('lines')
  const [showPeriodModal, setShowPeriodModal] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState('')

  // Obtener los detalles completos del presupuesto
  const { data: budget } = useQuery(
    ['budget', initialBudget.id],
    async () => {
      const response = await api.get(`/financial/budgets/${initialBudget.id}/`)
      return response.data
    },
    { 
      enabled: !!initialBudget.id,
      initialData: initialBudget, // Usar la prop como dato inicial
      onSuccess: () => {
        // Invalidar solo los presupuestos en general si es necesario
        // para refrescar listas que puedan estar mostrando datos parciales
        queryClient.invalidateQueries(['budgets'])
      }
    }
  )

  // ... (resto del código) ...

  // Obtener líneas del presupuesto
  const { data: lines, isLoading: linesLoading, refetch: refetchLines } = useQuery(
    ['budget-lines', budget.id],
    async () => {
      const response = await api.get(`/financial/lineas-presupuesto/?presupuesto=${budget.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!budget.id }
  )

  // Mutación para crear/actualizar línea
  const lineMutation = useMutation(
    async (lineData) => {
      if (lineData.id) {
        return api.put(`/financial/lineas-presupuesto/${lineData.id}/`, lineData)
      } else {
        return api.post('/financial/lineas-presupuesto/', lineData)
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['budget-lines', budget.id])
        await queryClient.invalidateQueries(['budgets'])
        setShowLineForm(false)
        setSelectedLine(null)
        toast.success(selectedLine ? 'Línea actualizada exitosamente' : 'Línea creada exitosamente')
        // Forzar refetch inmediato
        setTimeout(() => {
          refetchLines()
        }, 100)
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar línea'
        toast.error(message)
      }
    }
  )

  // Mutación para eliminar línea
  const deleteLineMutation = useMutation(
    async (lineId) => {
      return api.delete(`/financial/lineas-presupuesto/${lineId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budget-lines', budget.id])
        queryClient.invalidateQueries(['budgets'])
        toast.success('Línea eliminada exitosamente')
      },
      onError: () => {
        toast.error('Error al eliminar línea')
      }
    }
  )

  const handleCreateLine = () => {
    setSelectedLine(null)
    setShowLineForm(true)
  }

  const handleEditLine = (line) => {
    setSelectedLine(line)
    setShowLineForm(true)
  }

  const handleDeleteLine = (line) => {
    if (window.confirm(`¿Estás seguro de eliminar esta línea?`)) {
      deleteLineMutation.mutate(line.id)
    }
  }

  const handleSubmitLine = (formData) => {
    lineMutation.mutate({
      ...formData,
      presupuesto: budget.id
    })
  }

  // Mutación para calcular ejecución
  const calcularEjecucionMutation = useMutation(
    async (periodo) => {
      const data = periodo ? { periodo } : {}
      return api.post(`/financial/budgets/${budget.id}/calcular_ejecucion/`, data)
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['budget-lines', budget.id])
        queryClient.invalidateQueries(['budgets'])
        queryClient.invalidateQueries(['budget-execution', budget.id])
        toast.success(response.data.message || 'Ejecución calculada exitosamente')
        setShowPeriodModal(false)
        setSelectedPeriod('')
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al calcular ejecución'
        toast.error(message)
      }
    }
  )

  const handleCalcularEjecucion = () => {
    if (budget.estado !== 'vigente') {
      toast.error('Solo se puede calcular ejecución para presupuestos vigentes')
      return
    }
    setShowPeriodModal(true)
  }

  const handleConfirmCalcularEjecucion = () => {
    const periodo = selectedPeriod || getMonthStartDateOnlyMx()
    calcularEjecucionMutation.mutate(periodo)
  }

  // Mutación para exportar PDF
  const exportarPDFMutation = useMutation(
    async () => {
      const response = await api.get(`/financial/budgets/${budget.id}/reporte_pdf/`, {
        responseType: 'blob'
      })
      return response.data
    },
    {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `presupuesto_${budget.id}_reporte.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success('PDF generado y descargado exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al generar PDF'
        toast.error(message)
      }
    }
  )

  // Mutación para exportar Excel
  const exportarExcelMutation = useMutation(
    async () => {
      const response = await api.get(`/financial/budgets/${budget.id}/exportar_excel/`, {
        responseType: 'blob'
      })
      return response.data
    },
    {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `presupuesto_${budget.id}_ejecucion.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Excel generado y descargado exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al generar Excel'
        toast.error(message)
      }
    }
  )

  const handleExportarPDF = () => {
    exportarPDFMutation.mutate()
  }

  const handleExportarExcel = () => {
    exportarExcelMutation.mutate()
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'borrador': { bg: 'secondary', text: 'Borrador' },
      'en_aprobacion': { bg: 'warning', text: 'En Aprobación' },
      'vigente': { bg: 'success', text: 'Vigente' },
      'cerrado': { bg: 'dark', text: 'Cerrado' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      'anual': { bg: 'primary', text: 'Anual' },
      'proyecto': { bg: 'info', text: 'Proyecto' },
      'departamento': { bg: 'success', text: 'Departamento' },
      'otro': { bg: 'secondary', text: 'Otro' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const porcentaje = budget.porcentaje_ejecucion || 0
  const variant = porcentaje > 100 ? 'danger' : porcentaje > 80 ? 'warning' : 'success'

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>{budget.nombre}</h4>
          <div className="mt-2">
            {getTipoBadge(budget.tipo)} {getEstadoBadge(budget.estado)}
          </div>
        </div>
        <div className="d-flex gap-2">
          {budget.estado === 'vigente' && (
            <Button 
              variant="outline-success" 
              size="sm" 
              onClick={handleCalcularEjecucion}
              disabled={calcularEjecucionMutation.isLoading}
            >
              {calcularEjecucionMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Calculando...
                </>
              ) : (
                <>
                  <i className="bi bi-calculator me-1"></i>
                  Calcular Ejecución
                </>
              )}
            </Button>
          )}
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={handleExportarPDF}
            disabled={exportarPDFMutation.isLoading}
          >
            {exportarPDFMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-file-pdf me-1"></i>
                Exportar PDF
              </>
            )}
          </Button>
          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={handleExportarExcel}
            disabled={exportarExcelMutation.isLoading}
          >
            {exportarExcelMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-file-excel me-1"></i>
                Exportar Excel
              </>
            )}
          </Button>
          <Button variant="outline-primary" size="sm" onClick={onEdit}>
            Editar
          </Button>
        </div>
      </div>

      {/* Información general */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Cliente:</strong> {budget.cliente_name || 'N/A'}</p>
              <p><strong>Año:</strong> {budget.año}</p>
              <p><strong>Periodo:</strong> 
                {formatDateOnlyEsMX(budget.fecha_inicio, {}, '—')} - {formatDateOnlyEsMX(budget.fecha_fin, {}, '—')}
              </p>
              {budget.responsable_name && (
                <p><strong>Responsable:</strong> {budget.responsable_name}</p>
              )}
            </Col>
            <Col md={6}>
              <p><strong>Monto Presupuestado:</strong> 
                {formatCurrency(budget.monto_total || 0)}
              </p>
              <p><strong>Monto Ejecutado:</strong> 
                {formatCurrency(budget.monto_ejecutado || 0)}
              </p>
              <p><strong>Monto Comprometido:</strong> 
                {formatCurrency(budget.monto_comprometido || 0)}
              </p>
              <p><strong>% Ejecución:</strong> 
                <ProgressBar
                  variant={variant}
                  now={Math.min(porcentaje, 100)}
                  label={`${porcentaje.toFixed(1)}%`}
                  style={{ width: '200px', display: 'inline-block', marginLeft: '10px' }}
                />
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="lines" title="Líneas de Presupuesto">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Líneas de Presupuesto</h5>
            <Button variant="primary" size="sm" onClick={handleCreateLine}>
              + Agregar Línea
            </Button>
          </div>

          {linesLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" size="sm" />
              <span className="ms-2">Cargando líneas...</span>
            </div>
          ) : lines && lines.length > 0 ? (
            <Table responsive>
              <thead>
                <tr>
                  <th>Cuenta Contable</th>
                  <th>Presupuestado</th>
                  <th>Ejecutado</th>
                  <th>Comprometido</th>
                  <th>% Ejecución</th>
                  <th>Prioridad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const linePorcentaje = line.monto_presupuestado > 0
                    ? (line.monto_ejecutado / line.monto_presupuestado) * 100
                    : 0
                  const lineVariant = linePorcentaje > 100 ? 'danger' : linePorcentaje > 80 ? 'warning' : 'success'
                  return (
                    <tr key={line.id}>
                      <td>{line.cuenta_contable_code || line.cuenta_contable?.code || 'N/A'}</td>
                      <td>
                        {formatCurrency(line.monto_presupuestado || 0)}
                      </td>
                      <td>
                        {formatCurrency(line.monto_ejecutado || 0)}
                      </td>
                      <td>
                        {formatCurrency(line.monto_comprometido || 0)}
                      </td>
                      <td>
                        <ProgressBar
                          variant={lineVariant}
                          now={Math.min(linePorcentaje, 100)}
                          label={`${linePorcentaje.toFixed(1)}%`}
                          style={{ width: '100px' }}
                        />
                      </td>
                      <td>
                        <Badge bg={line.prioridad === 'critica' ? 'danger' : line.prioridad === 'alta' ? 'warning' : 'info'}>
                          {line.prioridad}
                        </Badge>
                      </td>
                      <td>
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleEditLine(line)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteLine(line)}
                            disabled={deleteLineMutation.isLoading}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              No hay líneas de presupuesto. Haz clic en &quot;Agregar Línea&quot; para comenzar.
            </Alert>
          )}
        </Tab>

        <Tab eventKey="execution" title="Ejecución">
          <BudgetExecution budget={budget} />
        </Tab>

        <Tab eventKey="modifications" title="Modificaciones">
          <BudgetModifications budget={budget} />
        </Tab>

        <Tab eventKey="alerts" title="Alertas">
          <BudgetAlerts budget={budget} />
        </Tab>

        <Tab eventKey="config" title="Configuración">
          <BudgetConfig budget={budget} />
        </Tab>

        <Tab eventKey="charts" title="Gráficos y Reportes">
          <BudgetCharts budget={budget} />
        </Tab>
      </Tabs>

      {/* Modal de línea */}
      <Modal show={showLineForm} onHide={() => { setShowLineForm(false); setSelectedLine(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedLine ? 'Editar Línea' : 'Nueva Línea'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BudgetLineForm
            line={selectedLine}
            budgetId={budget.id}
            clienteId={budget.cliente?.id ?? budget.cliente ?? budget.cliente_id}
            onSubmit={handleSubmitLine}
            onCancel={() => { setShowLineForm(false); setSelectedLine(null) }}
            isLoading={lineMutation.isLoading}
          />
        </Modal.Body>
      </Modal>

      {/* Modal para seleccionar período de cálculo */}
      <Modal show={showPeriodModal} onHide={() => setShowPeriodModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Calcular Ejecución</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Selecciona el período para calcular la ejecución. Si no especificas un período, se usará el mes actual.
          </Alert>
          <div className="mb-3">
            <label className="form-label">Período (opcional)</label>
            <input
              type="month"
              className="form-control"
              value={selectedPeriod ? selectedPeriod.substring(0, 7) : ''}
              onChange={(e) => setSelectedPeriod(e.target.value ? e.target.value + '-01' : '')}
              min={`${budget.año}-01`}
              max={`${budget.año}-12`}
            />
            <small className="form-text text-muted">
              Si no especificas un período, se calculará para el mes actual
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPeriodModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmCalcularEjecucion}
            disabled={calcularEjecucionMutation.isLoading}
          >
            {calcularEjecucionMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Calculando...
              </>
            ) : (
              'Calcular'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default BudgetDetail

